import { z } from 'zod';
import { createSchema } from 'zod-openapi';
import { ComparatorBillSchema } from './comparator.dto';

/**
 * The public comparator embedded in the ERIS website (GK-1059): no login,
 * protected by Cloudflare Turnstile and a per-IP budget. It compares with the
 * same engine and offers as the backoffice, shows one card per marketer, and
 * «Contratar» leaves a request in the approval tray.
 */

/** What the page needs before showing anything. */
export const PublicComparatorConfigSchema = z.object({
	/** Null when the public comparator is closed (production without Turnstile keys). */
	turnstileSiteKey: z.string().nullable(),
	/** False while no one is set to receive the requests: the cards hide «Contratar». */
	contractingEnabled: z.boolean(),
});

export const PublicCompareRequestSchema = z.object({
	bill: ComparatorBillSchema,
});

/** One card of the carousel: the marketer's best offer for this bill. */
export const PublicOfferCardSchema = z.object({
	offerId: z.number(),
	marketerName: z.string(),
	offerName: z.string(),
	/** The same month with this offer, with today's taxes. */
	monthTotal: z.number(),
	/** Positive = cheaper than the current bill. */
	savings: z.number(),
	savingsPct: z.number().nullable(),
});

export const PublicComparisonResponseSchema = z.object({
	tariff: z.string(),
	/** The current bill recomputed with today's taxes: what the cards compare against. */
	currentTotal: z.number(),
	/** Best savings first. */
	cards: z.array(PublicOfferCardSchema),
});

export const PUBLIC_CONTRACT_KINDS = ['INDIVIDUAL', 'SELF_EMPLOYED', 'COMPANY'] as const;

const text = (max: number) => z.string().trim().min(1, 'Este dato es obligatorio').max(max);

/**
 * «Contratar» (Montoro, 28/10): an individual or self-employed person gives
 * DNI, email, mobile and account number; a company gives CIF, representative's
 * DNI, email, mobile and account number. Both upload the DNI. The bill travels
 * again so the server recomputes the offer instead of trusting the browser.
 */
export const PublicContractRequestSchema = z
	.object({
		kind: z.enum(PUBLIC_CONTRACT_KINDS),
		/** Holder's full name, or the company name. */
		name: text(200),
		/** DNI/NIE, or the company's CIF. Control characters are checked on the server. */
		taxId: text(20),
		representativeName: z.string().trim().max(200).nullable().default(null),
		representativeTaxId: z.string().trim().max(20).nullable().default(null),
		email: z.string().trim().email('Revisa el correo').max(200),
		phone: z
			.string()
			.trim()
			.transform((value) => value.replace(/[\s.-]/g, ''))
			.pipe(z.string().regex(/^(\+34|0034)?[6789]\d{8}$/, 'Revisa el teléfono')),
		iban: z
			.string()
			.trim()
			.transform((value) => value.replace(/\s/g, '').toUpperCase())
			.pipe(z.string().regex(/^ES\d{22}$/, 'El número de cuenta tiene que ser un IBAN español (ES + 22 cifras)')),
		cupsCode: text(30),
		supplyAddress: z.string().trim().max(300).nullable().default(null),
		offerId: z.number().int().positive(),
		bill: ComparatorBillSchema,
		acceptPrivacy: z.literal(true, { message: 'Tienes que aceptar el tratamiento de tus datos' }),
	})
	.superRefine((value, context) => {
		if (value.kind !== 'COMPANY') return;
		if (!value.representativeName) {
			context.addIssue({ code: 'custom', path: ['representativeName'], message: 'Este dato es obligatorio' });
		}
		if (!value.representativeTaxId) {
			context.addIssue({ code: 'custom', path: ['representativeTaxId'], message: 'Este dato es obligatorio' });
		}
	});

/** The DNI (both sides, or a PDF): up to two files of 10 MB. */
export const PUBLIC_CONTRACT_MAX_FILES = 2;
export const PUBLIC_CONTRACT_FILE_MAX_BYTES = 10 * 1024 * 1024;
export const PUBLIC_CONTRACT_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'] as const;

/** Spanish IBAN control digits (ISO 13616, mod 97). */
export const isValidIban = (raw: string): boolean => {
	const iban = raw.replace(/\s/g, '').toUpperCase();
	if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(iban)) return false;
	const rearranged = `${iban.slice(4)}${iban.slice(0, 4)}`;
	const digits = rearranged.replace(/[A-Z]/g, (letter) => String(letter.charCodeAt(0) - 55));
	let remainder = 0;
	for (const digit of digits) remainder = (remainder * 10 + Number(digit)) % 97;
	return remainder === 1;
};

export type PublicComparatorConfigDTO = z.infer<typeof PublicComparatorConfigSchema>;
export type PublicCompareRequestDTO = z.output<typeof PublicCompareRequestSchema>;
export type PublicCompareRequestInput = z.input<typeof PublicCompareRequestSchema>;
export type PublicOfferCardDTO = z.infer<typeof PublicOfferCardSchema>;
export type PublicComparisonResponseDTO = z.infer<typeof PublicComparisonResponseSchema>;
export type PublicContractKind = (typeof PUBLIC_CONTRACT_KINDS)[number];
export type PublicContractRequestDTO = z.output<typeof PublicContractRequestSchema>;
export type PublicContractRequestInput = z.input<typeof PublicContractRequestSchema>;

export const { schema: PublicComparatorConfigOpenAPI } = createSchema(PublicComparatorConfigSchema);
export const { schema: PublicCompareRequestOpenAPI } = createSchema(PublicCompareRequestSchema);
export const { schema: PublicComparisonResponseOpenAPI } = createSchema(PublicComparisonResponseSchema);
